import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowBackRounded,
  ScienceRounded,
  AddPhotoAlternateRounded,
  DeleteOutlineRounded,
  CloseRounded,
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { getMyProjects, createProject, deleteProject } from '../services/api';
import { StudentProjectItem } from '../types';

const IMG_BASE = process.env.REACT_APP_BACKEND_BASE_URL || '';

const fmtDate = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const MyProjects: React.FC = () => {
  const navigate = useNavigate();
  const [list, setList] = useState<StudentProjectItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // create form
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadList = async () => {
    setLoading(true);
    setError(null);
    try {
      setList(await getMyProjects());
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  const pickFile = (f: File | null) => {
    setFile(f);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(f ? URL.createObjectURL(f) : null);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await createProject(title.trim(), description.trim() || undefined, file);
      setList((prev) => [created, ...prev]);
      setTitle('');
      setDescription('');
      pickFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (e: any) {
      setError(e.message || 'Failed to share project');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id: number) => {
    setBusyId(id);
    setError(null);
    try {
      await deleteProject(id);
      setList((prev) => prev.filter((p) => p.id !== id));
    } catch (e: any) {
      setError(e.message || 'Failed to delete project');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-6">
      <button
        onClick={() => navigate('/chat')}
        className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowBackRounded fontSize="small" /> Back to chat
      </button>
      <h1 className="text-xl font-bold text-slate-900">My Projects</h1>
      <p className="mb-5 text-sm text-slate-500">
        Made something? Share your project here — add a photo so everyone can see what you built.
      </p>

      {/* Share a project */}
      <form onSubmit={submit} className="mb-6 rounded-2xl border border-[#E9E6F4] bg-white p-4">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          placeholder="What did you make? (e.g. Volcano model)"
          className="mb-3 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-violet-400 focus:outline-none"
        />
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder="Tell us about it (optional)…"
          className="mb-3 w-full resize-none rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-violet-400 focus:outline-none"
        />
        {preview && (
          <div className="relative mb-3 inline-block">
            <img src={preview} alt="preview" className="max-h-40 rounded-xl border border-slate-200" />
            <button
              type="button"
              onClick={() => {
                pickFile(null);
                if (fileRef.current) fileRef.current.value = '';
              }}
              className="absolute -right-2 -top-2 rounded-full bg-slate-700 p-0.5 text-white"
              aria-label="Remove photo"
            >
              <CloseRounded sx={{ fontSize: 16 }} />
            </button>
          </div>
        )}
        <div className="flex items-center justify-between gap-3">
          <label className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-violet-700 hover:text-violet-800">
            <AddPhotoAlternateRounded fontSize="small" />
            {file ? 'Change photo' : 'Add a photo'}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0] || null)}
            />
          </label>
          <button
            type="submit"
            disabled={!title.trim() || submitting}
            className="rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
          >
            {submitting ? 'Sharing…' : 'Share project'}
          </button>
        </div>
      </form>

      {error && <p className="mb-4 text-sm text-rose-600">{error}</p>}

      {loading ? (
        <p className="py-10 text-center text-slate-500">Loading…</p>
      ) : list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-slate-500">
          <ScienceRounded className="mb-2 text-slate-300" />
          <p className="text-sm">No projects yet — share your first one above! 🔬</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {list.map((p) => (
            <div key={p.id} className="rounded-xl border border-[#E9E6F4] bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800">{p.title}</p>
                  <p className="text-[11px] text-slate-400">Shared {fmtDate(p.shared_at)}</p>
                </div>
                <button
                  onClick={() => remove(p.id)}
                  disabled={busyId === p.id}
                  className="flex-none text-slate-300 hover:text-rose-500 disabled:opacity-50"
                  aria-label="Delete project"
                >
                  {busyId === p.id ? (
                    <CircularProgress size={18} sx={{ color: '#f43f5e' }} />
                  ) : (
                    <DeleteOutlineRounded fontSize="small" />
                  )}
                </button>
              </div>
              {p.description && <p className="mt-1.5 text-sm text-slate-600">{p.description}</p>}
              {p.image_url && (
                <img
                  src={`${IMG_BASE}${p.image_url}`}
                  alt={p.title}
                  className="mt-3 max-h-64 w-full rounded-xl border border-slate-100 object-cover"
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyProjects;
